import React, { useEffect, useState, useCallback, useRef } from "react";
import { Save, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { Pager, usePaged } from "../components/Pager";
import { api, formatApiError } from "../lib/api";

const fmtGbp = (v) =>
  v == null || v === "" ? "—" : `£${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function IncomeLedger() {
  const [projects, setProjects] = useState([]);
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState(thisMonth());
  const [cells, setCells] = useState({});   // { pid: {billing, export, opex} }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const downloadTemplate = () => {
    const csv = "Site,Month,Billing,Export,OPEX\nExample Site Name,2026-01,2500,850,260\n";
    const dl = document.createElement("a");
    dl.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    dl.download = "income_import_template.csv";
    document.body.appendChild(dl); dl.click();
    setTimeout(() => { URL.revokeObjectURL(dl.href); dl.remove(); }, 200);
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/income/import-csv", fd);
      let msg = `Imported ${data.imported} row${data.imported === 1 ? "" : "s"}`;
      if (data.skipped) msg += ` · skipped ${data.skipped}`;
      toast.success(msg);
      if (data.unmatched_sites?.length) {
        toast.warning(`Unmatched sites: ${data.unmatched_sites.join(", ")}`);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/income/portfolio?months=12`);
      setProjects(data.projects || []);
      setMonths(data.months || []);
      const next = {};
      (data.projects || []).forEach((p) => {
        const e = (data.entries || {})[p.id]?.[month] || {};
        next[p.id] = {
          billing: e.billing_income_gbp ?? "",
          export: e.export_income_gbp ?? "",
          rental: e.rental_income_gbp ?? "",
          fit: e.fit_income_gbp ?? "",
          opex: e.opex_gbp ?? "",
        };
      });
      setCells(next);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const setCell = (pid, field, val) =>
    setCells((prev) => ({ ...prev, [pid]: { ...prev[pid], [field]: val } }));

  const saveAll = async () => {
    const items = [];
    projects.forEach((p) => {
      const c = cells[p.id] || {};
      const hasAny = c.billing !== "" || c.export !== "" || c.rental !== "" || c.fit !== "" || c.opex !== "";
      if (!hasAny) return;
      items.push({
        project_id: p.id, month,
        billing_income_gbp: Number(c.billing) || 0,
        export_income_gbp: Number(c.export) || 0,
        rental_income_gbp: Number(c.rental) || 0,
        fit_income_gbp: Number(c.fit) || 0,
        opex_gbp: c.opex === "" ? null : Number(c.opex),
      });
    });
    if (items.length === 0) { toast.error("Nothing to save"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/income/portfolio`, items);
      toast.success(`Saved ${data.saved} site${data.saved === 1 ? "" : "s"} for ${month}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const totalFor = (field) =>
    projects.reduce((a, p) => a + (Number(cells[p.id]?.[field]) || 0), 0);

  const ledgerPage = usePaged(projects, "eneprox.ledger.perPage", projects.length);
  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="income-ledger-page">
      <div className="mb-8">
        <div className="label mb-2">Reporting</div>
        <h1 className="font-display text-4xl font-bold">Actual income ledger</h1>
        <p className="muted mt-2 text-sm">
          Record real billing + export income per site, per month. This becomes the basis for
          every site's Yield-on-Cost and IRR tracking.
        </p>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <label className="flex items-center gap-2">
          <span className="label">Month</span>
          <input type="month" className="input-swiss" value={month} max={thisMonth()}
                 onChange={(e) => setMonth(e.target.value)} data-testid="ledger-month-select" />
        </label>
        <div className="flex items-center gap-2 flex-wrap">
        <button onClick={saveAll} disabled={saving} className="btn-primary" data-testid="ledger-save-btn">
          <Save size={14} /> {saving ? "Saving…" : "Save all for month"}
        </button>
        <button onClick={downloadTemplate} className="btn-ghost text-xs" data-testid="ledger-template-btn">
          <Download size={13} /> Template
        </button>
        <button onClick={() => fileRef.current?.click()} disabled={importing}
                className="btn-ghost text-xs" data-testid="ledger-import-btn">
          <Upload size={13} /> {importing ? "Importing…" : "Import CSV / Excel"}
        </button>
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" className="hidden"
               onChange={importCsv} data-testid="ledger-import-input" />
        </div>
      </div>

      {loading ? (
        <div className="muted text-sm">Loading sites…</div>
      ) : projects.length === 0 ? (
        <div className="muted text-sm">No projects found.</div>
      ) : (
        <div className="swiss-card-flat">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ledger-table">
            <thead>
              <tr className="text-left label border-b border-[var(--line)] bg-[var(--surface-2)]">
                <th className="py-3 px-4">Site</th>
                <th className="py-3 px-4 text-right">Billing £</th>
                <th className="py-3 px-4 text-right">Export £</th>
                <th className="py-3 px-4 text-right">Rental £</th>
                <th className="py-3 px-4 text-right">FIT £</th>
                <th className="py-3 px-4 text-right">OPEX £ (opt)</th>
                <th className="py-3 px-4 text-right">Total £</th>
              </tr>
            </thead>
            <tbody>
              {ledgerPage.rows.map((p) => {
                const c = cells[p.id] || {};
                const tot = (Number(c.billing) || 0) + (Number(c.export) || 0) + (Number(c.rental) || 0) + (Number(c.fit) || 0);
                return (
                  <tr key={p.id} className="border-b border-[var(--line)]" data-testid={`ledger-row-${p.id}`}>
                    <td className="py-2 px-4 font-medium">
                      {p.name}
                      {p.is_rentalised && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-[var(--accent-soft,#DDF3F1)] text-[var(--accent-hover)] align-middle">RENT</span>}
                      {p.fit_export && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 align-middle">FIT</span>}
                    </td>
                    <td className="py-2 px-4">
                      <input type="number" step="0.01" className="input-swiss text-right font-mono text-xs w-28 ml-auto block"
                             value={c.billing ?? ""} onChange={(e) => setCell(p.id, "billing", e.target.value)}
                             data-testid={`ledger-billing-${p.id}`} />
                    </td>
                    <td className="py-2 px-4">
                      <input type="number" step="0.01" className="input-swiss text-right font-mono text-xs w-28 ml-auto block"
                             value={c.export ?? ""} onChange={(e) => setCell(p.id, "export", e.target.value)}
                             data-testid={`ledger-export-${p.id}`} />
                    </td>
                    <td className="py-2 px-4">
                      <input type="number" step="0.01" className="input-swiss text-right font-mono text-xs w-28 ml-auto block"
                             value={c.rental ?? ""} onChange={(e) => setCell(p.id, "rental", e.target.value)}
                             data-testid={`ledger-rental-${p.id}`} />
                    </td>
                    <td className="py-2 px-4">
                      <input type="number" step="0.01" className="input-swiss text-right font-mono text-xs w-28 ml-auto block"
                             value={c.fit ?? ""} onChange={(e) => setCell(p.id, "fit", e.target.value)}
                             data-testid={`ledger-fit-${p.id}`} />
                    </td>
                    <td className="py-2 px-4">
                      <input type="number" step="0.01" className="input-swiss text-right font-mono text-xs w-28 ml-auto block"
                             value={c.opex ?? ""} placeholder="modelled"
                             onChange={(e) => setCell(p.id, "opex", e.target.value)}
                             data-testid={`ledger-opex-${p.id}`} />
                    </td>
                    <td className="py-2 px-4 text-right font-mono font-bold">{fmtGbp(tot)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[var(--ink)] font-mono font-bold">
                <td className="py-3 px-4 label">Portfolio total</td>
                <td className="py-3 px-4 text-right">{fmtGbp(totalFor("billing"))}</td>
                <td className="py-3 px-4 text-right">{fmtGbp(totalFor("export"))}</td>
                <td className="py-3 px-4 text-right">{fmtGbp(totalFor("rental"))}</td>
                <td className="py-3 px-4 text-right">{fmtGbp(totalFor("fit"))}</td>
                <td className="py-3 px-4 text-right">{fmtGbp(totalFor("opex"))}</td>
                <td className="py-3 px-4 text-right" data-testid="ledger-grand-total">
                  {fmtGbp(totalFor("billing") + totalFor("export") + totalFor("rental") + totalFor("fit"))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
          {projects.length > 0 && <Pager {...ledgerPage.pagerProps} testid="ledger-pager" />}
        </div>
      )}
    </div>
  );
}
